import { Transition } from "@headlessui/react";
import Button from "components/UI-lib/Button";
import FlatCard from "components/UI-lib/FlatCard";
import {
  classNames,
  displayPermissionHierarchyType,
  timeout,
} from "components/utils";
import { UserContext } from "contexts/UserContext";
import { PermissionHierarchyType } from "models/Permission.model";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { BarList } from "../../../Graphs/BarList";
import {
  ArrowTrendingUpIcon,
  ArrowUpIcon,
  EyeIcon,
  RocketLaunchIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import {
  ArrowPathIcon,
  ArrowUpRightIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/solid";
import { FireIcon } from "@heroicons/react/24/outline";
import { useHistory } from "react-router";
import LaunchableMeta from "./LaunchableMeta";
import { LaunchableContext } from "contexts/LaunchableContext";
import { Capability, OrgContext } from "contexts/OrgContext";
import { Button as NewButton } from "components/Graphs/Button";
import { BarChart2 } from "lucide-react";
import {
  LaunchableAnalytics,
  LaunchableAnalyticsTimeSeries,
  LaunchableCountsPerUser,
} from "server";
import agent from "server";
import UsernameDisplayField, {
  UsernameDisplayCircle,
} from "components/UsernameDisplayField";
import { Skeleton } from "@mui/material";
import { Spinner } from "components/UI-lib";
import {
  aggregateTotalsForLaunchables,
  getDailyTimeSeriesForLaunchables,
  getLaunchableTotals,
  getLaunchableTotalsPerUser,
  mapAggregateLaunchableMetricsResponseToLaunchableCountsPerUser,
} from "../utils/chartUtils";
import { useQuery } from "@tanstack/react-query";

export const LaunchableOverview: React.FC = () => {
  const history = useHistory();
  const launchableContext = useContext(LaunchableContext);
  const orgContext = useContext(OrgContext);

  const { data: userMetricsList = [], isLoading: loadingUserMetrics } =
    useQuery({
      queryKey: [
        "launchable-user-metrics",
        launchableContext.activelaunchables.map((l) => l.createdByUserId),
      ],
      queryFn: async () => {
        if (launchableContext.activelaunchables.length === 0) return [];
        const metrics = await getLaunchableTotalsPerUser(
          launchableContext.activelaunchables.map(
            (l) => l.createdByUserId || ""
          )
        );
        if (metrics.data) {
          return mapAggregateLaunchableMetricsResponseToLaunchableCountsPerUser(
            metrics.data,
            launchableContext.activelaunchables.map(
              (l) => l.createdByUserId || ""
            )
          );
        }
        return [];
      },
      enabled:
        launchableContext.activelaunchables.length > 0 ||
        !!orgContext.activeOrgId,
    });

  const {
    data: launchableMetrics = null,
    isLoading: loadingLaunchableMetrics,
  } = useQuery({
    queryKey: [
      "launchable-metric-totals",
      launchableContext.activelaunchables.map((l) => l.id),
    ],
    queryFn: async () => {
      if (launchableContext.activelaunchables.length === 0) return null;
      const metrics = await getLaunchableTotals(
        launchableContext.activelaunchables.map((l) => l.id)
      );
      if (metrics.success) {
        return aggregateTotalsForLaunchables(metrics.data);
      }
      return null;
    },
    enabled:
      launchableContext.activelaunchables.length > 0 ||
      !!orgContext.activeOrgId,
  });

  const {
    data: currentDayMetricsTotals = { views: 0, deploys: 0 },
    isLoading: loadingCurrentDayMetricsTotals,
  } = useQuery({
    queryKey: [
      "launchable-daily-metrics",
      launchableContext.activelaunchables.map((l) => l.id),
    ],
    queryFn: async () => {
      if (launchableContext.activelaunchables.length === 0) {
        return { views: 0, deploys: 0 };
      }
      const metrics = await getDailyTimeSeriesForLaunchables(
        launchableContext.activelaunchables.map((l) => l.id),
        new Date(new Date().setDate(new Date().getDate() - 7)),
        new Date(new Date().setHours(23, 59, 59, 999))
      );
      if (metrics.data) {
        return aggregateTotalsForLaunchables(metrics.data);
      }
      return { views: 0, deploys: 0 };
    },
    enabled:
      launchableContext.activelaunchables.length > 0 ||
      !!orgContext.activeOrgId,
  });

  const sortedUserMetricsList = useMemo(() => {
    return userMetricsList.sort((a, b) => {
      if (a.analytics.deploys === b.analytics.deploys) {
        return b.analytics.views - a.analytics.views;
      }
      return b.analytics.deploys - a.analytics.deploys;
    });
  }, [userMetricsList]);

  return (
    <>
      {/* Preview Cards - What they'll track */}
      <div className="flex flex-col">
        <div className="flex flex-row justify-end mb-1">
          <NewButton
            variant="ghost"
            onClick={() => {
              history.push(
                `/org/${orgContext.activeOrgId}/launchables/metrics`
              );
            }}
          >
            <BarChart2 className="w-4 h-4 mr-1 text-highlight" />
            <span className="text-highlight font-bold">View All Metrics</span>
          </NewButton>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            className="flex flex-col bg-primaryBg border border-zinc-800 dark:text-secondary rounded-md p-4"
            style={{
              background:
                "linear-gradient(268.46deg, rgba(118, 185, 0, 0.3) -1.19%, rgba(118, 185, 0, 0.147) 96.91%)",
            }}
          >
            <div className="flex flex-col">
              <div className="flex flex-col mb-1">
                <span className="text-secondary font-bold">Team Champion</span>
              </div>
              <div className="flex flex-row">
                {loadingUserMetrics ? (
                  <div className="flex flex-row justify-start items-center h-8">
                    <Spinner type="primary" />
                  </div>
                ) : sortedUserMetricsList.length > 0 ? (
                  <div className="flex flex-row justify-start items-center">
                    <UsernameDisplayCircle
                      userId={sortedUserMetricsList[0].creator_id}
                      className="w-9 h-9 mr-1 bg-white text-black"
                    />
                    <div className="flex flex-col">
                      <span className="text-white font-semibold text-sm">
                        <UsernameDisplayField
                          className="truncate whitespace-nowrap text-xl"
                          userId={sortedUserMetricsList[0].creator_id}
                        />
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-row justify-start items-center">
                    <div
                      className={
                        "h-9 w-9 rounded-full border border-zinc-800 flex items-center justify-center text-white bg-white"
                      }
                    ></div>
                    <div className="flex flex-col">
                      <span className="text-white font-semibold ml-1">
                        Coming soon
                      </span>
                      <span className="text-secondary font-semibold ml-1 text-xs">
                        Your Top Performer
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col bg-primaryBg border border-zinc-800 dark:text-secondary rounded-md p-4">
            <div className="flex flex-row ">
              <div className="flex flex-col w-full">
                <span className="text-secondary font-bold">Total Views</span>
                {loadingLaunchableMetrics ? (
                  <div className="flex flex-row justify-start items-center h-8">
                    <Spinner type="primary" />
                  </div>
                ) : launchableMetrics ? (
                  <div className="flex flex-row mt-3 w-full justify-between">
                    <div className="flex flex-row justify-start items-center">
                      <EyeIcon className="w-6 h-6 mr-2 text-highlight" />
                      <span className="text-white font-bold text-xl">
                        {launchableMetrics.views}
                      </span>
                    </div>
                    {currentDayMetricsTotals.views > 0 && (
                      <div className="flex flex-row justify-end items-center mt-1">
                        <span className="text-highlight font-bold text-xs">
                          +{currentDayMetricsTotals.views}
                          <span className="text-secondary font-bold text-xs ml-1">
                            Last 7 days
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-row mt-3 w-full justify-between">
                    <div className="flex flex-row justify-start items-center">
                      <EyeIcon className="w-6 h-6 mr-2 text-highlight" />
                      <span className="text-white font-bold text-xl">0</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col bg-primaryBg border border-zinc-800 dark:text-secondary rounded-md p-4">
            <div className="flex flex-row">
              <div className="flex flex-col w-full">
                <span className="text-secondary font-bold">Total Deploys</span>
                {loadingLaunchableMetrics ? (
                  <div className="flex flex-row justify-start items-center h-8">
                    <Spinner type="primary" />
                  </div>
                ) : launchableMetrics ? (
                  <div className="flex flex-row mt-3 w-full justify-between">
                    <div className="flex flex-row justify-start items-center">
                      <RocketLaunchIcon className="w-6 h-6 mr-2 text-highlight" />
                      <span className="text-white font-bold text-xl">
                        {launchableMetrics.deploys}
                      </span>
                    </div>
                    {currentDayMetricsTotals.views > 0 && (
                      <div className="flex flex-row justify-end items-center mt-1">
                        <span className="text-highlight font-bold text-xs">
                          +{currentDayMetricsTotals.deploys}
                          <span className="text-secondary font-bold text-xs ml-1">
                            Last 7 days
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-row mt-3 w-full justify-between">
                    <div className="flex flex-row justify-start items-center">
                      <RocketLaunchIcon className="w-6 h-6 mr-2 text-highlight" />
                      <span className="text-white font-bold text-xl">0</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

interface LaunchableOverviewSkeletonProps {
  type: "view" | "deploy";
  loading: boolean;
  launchables: any[];
}

export const LaunchableOverviewSkeleton: React.FC<
  LaunchableOverviewSkeletonProps
> = (props) => {
  const viewData1 = [
    { name: "NeMo", value: 843 },
    { name: "Triton", value: 252 },
    { name: "TensorRT", value: 25 },
  ];
  const deployData1 = [
    { name: "NeMo", value: 206 },
    { name: "Triton", value: 123 },
    { name: "TensorRT", value: 19 },
  ];

  const viewData2 = [
    { name: "Llama3", value: 439 },
    { name: "Claude", value: 234 },
    { name: "Mistral", value: 10 },
  ];
  const deployData2 = [
    { name: "Llama3", value: 206 },
    { name: "Claude", value: 113 },
    { name: "Mistral", value: 4 },
  ];

  const [shownData, setShownData] = useState(
    props.type === "view" ? viewData1 : deployData1
  );

  const animateLaunchableSkeleton = async () => {
    setShownData(props.type === "view" ? viewData2 : deployData2);
    await timeout(5000);
    setShownData(props.type === "view" ? viewData1 : deployData1);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      animateLaunchableSkeleton();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <div className="flex flex-row w-full mt-3 relative">
        <div className="absolute top-[35%] left-[35%] z-10">
          <span className="text-white font-bold">
            {props.loading ? (
              "Loading..."
            ) : (
              <>
                {props.launchables.length <= 0
                  ? "No Launchables Yet"
                  : "No Metrics Available"}
              </>
            )}
          </span>
        </div>
        <div className="opacity-25 w-full">
          <BarList data={shownData} className="w-full" showAnimation={true} />
        </div>
      </div>
    </>
  );
};
